import type { ReactElement } from 'react';
import JsxParser, { type TProps } from 'react-jsx-parser';
import { COMPONENT_MAP } from 'brd-react-components';
import { createLogger } from '@/lib/logger';
import { normalizeCmsReactMarkup } from './normalize';

const logger = createLogger();

interface ReactFragmentProps {
    /** JSX string from a content asset — components must be registered in COMPONENT_MAP */
    content: string;
    /** Optional CSS class applied to the wrapper element */
    className?: string;
    /** Optional values available as variables inside the JSX string (e.g. Page Designer props) */
    bindings?: Record<string, unknown>;
}

function handleError(error: Error): void {
    logger.error('Failed to parse React content asset', { error });
}

/**
 * Renders JSX from a content asset string by mapping component names to registered components.
 * SSR-safe — runs on both server and client without any eval/new Function.
 * Only supports static JSX (string/boolean/number props). For plain HTML, use HtmlFragment instead.
 * Add components to COMPONENT_MAP to make them available in content assets.
 */
export default function ReactFragment({ content, className, bindings }: ReactFragmentProps): ReactElement {
    const jsx = normalizeCmsReactMarkup(content);

    return (
        <div data-testid="react-fragment" className={className}>
            <JsxParser
                jsx={jsx}
                components={COMPONENT_MAP as unknown as TProps['components']}
                bindings={bindings}
                renderInWrapper={false}
                allowUnknownElements
                onError={handleError}
            />
        </div>
    );
}

export { isReactContent } from './detect';
